import {
  type MouseEvent,
  type PointerEvent,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {
  AlertTriangle,
  CheckIcon,
  EllipsisVertical,
  InfoIcon,
  X,
} from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/Dropdown';
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import useTMSContext from 'hooks/useTMSContext';
import { Maybe } from 'types/UtilityTypes';
import { GenericSuggestion } from 'types/suggestions/CoreSuggestions';
import { getDrumkitContainer } from 'utils/getDrumkitContainer';
import { cn } from 'utils/shadcn';

import {
  KIT_PREVIEW_MAX_WIDTH_PX,
  KIT_PREVIEW_MIN_WIDTH_PX,
  KIT_PREVIEW_OPEN_DELAY_MS,
} from '../constants';
import {
  getKitSuggestionApplyBlockedReason,
  isBlockedSpecialTransportSuggestion,
  isKitSuggestionApplyBlocked,
} from '../utils/kitSuggestionEligibility';
import { SuggestionPreview } from './SuggestionPreview';

type SuggestionProps = {
  suggestion?: GenericSuggestion;
  completed?: boolean;
  dismissible?: boolean;
  /** List ordinal including trailing period, e.g. "1." */
  ordinal: string;
  /** Human-readable body without the ordinal */
  descriptor: string;
  /** Combined title for aria-labels / native tooltips */
  fullTitle: string;
  disabled: boolean;
  isSelected: boolean;
  /** Controlled: only one Preview open across Kit at a time. */
  isPreviewOpen: boolean;
  onPreviewOpenChange: (open: boolean) => void;
  /** Close a foreign Preview when the pointer enters this row. */
  onRowPointerEnter: () => void;
  onApply: (e: MouseEvent<HTMLButtonElement>) => void;
  onDismiss: () => void | Promise<void>;
};

type PreviewLayout = {
  /** Tooltip width: category tree line -> info-icon column left */
  widthPx: number;
  /** Shift end-aligned tooltip so its right edge meets the info column */
  alignOffsetPx: number;
};

// Individual suggestion: pill selects/applies; Preview + ... appear on row hover.
export function Suggestion({
  suggestion,
  completed,
  dismissible = true,
  ordinal,
  descriptor,
  fullTitle,
  disabled,
  isSelected,
  isPreviewOpen,
  onPreviewOpenChange,
  onRowPointerEnter,
  onApply,
  onDismiss,
}: SuggestionProps) {
  const { tmsName } = useTMSContext();
  const isDone = completed ?? disabled;
  const isSpecialTransportBlocked =
    suggestion != null && isBlockedSpecialTransportSuggestion(suggestion);
  const applyBlockedReason = suggestion
    ? getKitSuggestionApplyBlockedReason(suggestion, tmsName)
    : null;
  const isApplyBlocked =
    suggestion != null && isKitSuggestionApplyBlocked(suggestion, tmsName);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const previewTriggerRef = useRef<HTMLButtonElement>(null);
  const previewOpenTimerRef =
    useRef<Maybe<ReturnType<typeof setTimeout>>>(null);
  const [previewLayout, setPreviewLayout] = useState<PreviewLayout>({
    widthPx: KIT_PREVIEW_MAX_WIDTH_PX,
    alignOffsetPx: 0,
  });

  const showHoverActions = menuOpen || isPreviewOpen;
  let suggestionStateClass =
    'border-neutral-300 bg-neutral-50 text-neutral-700 hover:bg-neutral-100 hover:border-neutral-400';
  if (isDone)
    suggestionStateClass =
      'cursor-default border-neutral-300 bg-neutral-100 text-neutral-500';
  else if (isSpecialTransportBlocked)
    suggestionStateClass =
      'cursor-not-allowed border-error-500 bg-error-50 text-neutral-700';
  else if (isApplyBlocked)
    suggestionStateClass =
      'cursor-not-allowed border-warning-500 bg-warning-50 text-neutral-700';
  else if (isSelected)
    suggestionStateClass = 'border-accent-400 bg-accent-50/60';

  const clearPreviewOpenTimer = () => {
    if (previewOpenTimerRef.current != null) {
      clearTimeout(previewOpenTimerRef.current);
      previewOpenTimerRef.current = null;
    }
  };

  const openPreview = () => {
    if (menuOpen) return;
    clearPreviewOpenTimer();
    previewOpenTimerRef.current = setTimeout(() => {
      previewOpenTimerRef.current = null;
      onPreviewOpenChange(true);
    }, KIT_PREVIEW_OPEN_DELAY_MS);
  };

  /**
   * Hover actions use `pointer-events-none` until the row is hovered. Landing on
   * a new row with the cursor already over the info icon does not fire
   * pointerenter on the button - detect that and open Preview explicitly.
   */
  const handleRowPointerEnter = (e: PointerEvent<HTMLDivElement>) => {
    onRowPointerEnter();
    const { clientX, clientY } = e;
    requestAnimationFrame(() => {
      const trigger = previewTriggerRef.current;
      if (!trigger || menuOpen) return;
      const top = document.elementFromPoint(clientX, clientY);
      if (top && (trigger === top || trigger.contains(top))) {
        openPreview();
      }
    });
  };

  useLayoutEffect(() => {
    return () => clearPreviewOpenTimer();
  }, []);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const actions = actionsRef.current;
    const trigger = previewTriggerRef.current;
    if (!row || !actions || !trigger) return;

    let frameId: number | null = null;
    const measure = () => {
      if (frameId !== null) {
        return;
      }
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        const rowLeft = row.getBoundingClientRect().left;
        const actionsLeft = actions.getBoundingClientRect().left;
        const triggerRight = trigger.getBoundingClientRect().right;
        // Extend to the category tree line (border-l), not the pill's ml-4 inset.
        const tree = row.closest('[data-kit-category-tree]');
        const leftBound = tree ? tree.getBoundingClientRect().left : rowLeft;

        const widthPx = Math.min(
          KIT_PREVIEW_MAX_WIDTH_PX,
          Math.max(
            KIT_PREVIEW_MIN_WIDTH_PX,
            Math.floor(actionsLeft - leftBound)
          )
        );
        // bottom-end: positive alignOffset shifts toward start (left), so the
        // tooltip's right edge lands on the info-icon column instead of the
        // trigger's right edge.
        const alignOffsetPx = Math.max(
          0,
          Math.round(triggerRight - actionsLeft)
        );

        setPreviewLayout((prev) =>
          prev.widthPx === widthPx && prev.alignOffsetPx === alignOffsetPx
            ? prev
            : { widthPx, alignOffsetPx }
        );
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      window.removeEventListener('resize', measure);
    };
  }, [showHoverActions, isPreviewOpen]);

  const handleDismiss = async (e: Event) => {
    e.preventDefault();
    if (isDismissing) return;
    setIsDismissing(true);
    try {
      await onDismiss();
    } finally {
      setIsDismissing(false);
      setMenuOpen(false);
    }
  };

  return (
    <div
      ref={rowRef}
      className={cn(
        'group/suggestion relative max-w-full w-full ml-4 min-w-0',
        showHoverActions && 'z-10'
      )}
      onPointerEnter={handleRowPointerEnter}
    >
      <button
        type='button'
        disabled={disabled || isApplyBlocked}
        onClick={(e) => {
          if (disabled || isApplyBlocked) {
            e.stopPropagation();
            return;
          }
          onApply(e);
        }}
        className={cn(
          'max-w-full w-full rounded border pl-2.5 pr-3 py-1.5 text-left text-xs transition-all overflow-hidden min-w-0 cursor-pointer',
          // Reserve room for absolute info/menu actions so they don't cover
          // trailing content (warning icon on blocked pills, truncated text).
          !isDone && 'group-hover/suggestion:pr-14',
          showHoverActions && !isDone && 'pr-14',
          suggestionStateClass
        )}
        aria-label={
          isApplyBlocked
            ? `Cannot apply ${fullTitle}: ${applyBlockedReason}`
            : `${suggestion ? 'Apply' : 'Select'} ${fullTitle} suggestion`
        }
        title={isApplyBlocked ? (applyBlockedReason ?? fullTitle) : fullTitle}
      >
        <Flex align='center' gap='xs' className='min-w-0 w-full'>
          <Typography
            variant='body-xxs'
            className='shrink-0 tabular-nums text-neutral-400'
          >
            {ordinal}
          </Typography>
          <Typography
            variant='body-xxs'
            className={cn(
              'min-w-0 flex-1 truncate',
              isDone && 'text-neutral-500'
            )}
          >
            {descriptor}
          </Typography>
          {isApplyBlocked && !isDone && applyBlockedReason && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className='shrink-0 inline-flex'
                  onClick={(e) => e.stopPropagation()}
                >
                  <AlertTriangle
                    className={cn(
                      'h-3.5 w-3.5',
                      isSpecialTransportBlocked
                        ? 'text-error-600'
                        : 'text-warning-600'
                    )}
                    strokeWidth={2}
                    aria-hidden
                  />
                </span>
              </TooltipTrigger>
              <TooltipPortal container={getDrumkitContainer()}>
                <TooltipContent side='left' className='max-w-[220px]'>
                  <Typography variant='body-xs'>
                    {isSpecialTransportBlocked
                      ? 'Special Transport'
                      : applyBlockedReason}
                  </Typography>
                </TooltipContent>
              </TooltipPortal>
            </Tooltip>
          )}
          {isDone && (
            <Flex align='center' gap='xs' className='shrink-0'>
              <Typography
                variant='body-xxs'
                weight='medium'
                className='text-accent'
              >
                Done
              </Typography>
              <CheckIcon className='h-3 w-3 !stroke-accent' strokeWidth={2} />
            </Flex>
          )}
        </Flex>
      </button>

      {!isDone && (suggestion || dismissible) && (
        <div
          ref={actionsRef}
          className={cn(
            'absolute right-1 top-1/2 -translate-y-1/2',
            'flex items-center gap-0.5',
            'translate-x-2 opacity-0 pointer-events-none',
            'transition-all duration-150 ease-out',
            'group-hover/suggestion:translate-x-0 group-hover/suggestion:opacity-100 group-hover/suggestion:pointer-events-auto',
            showHoverActions && 'translate-x-0 opacity-100 pointer-events-auto'
          )}
        >
          {suggestion && (
            <Tooltip
              delayDuration={KIT_PREVIEW_OPEN_DELAY_MS}
              open={menuOpen ? false : isPreviewOpen}
              onOpenChange={(open) => {
                if (menuOpen) return;
                onPreviewOpenChange(open);
              }}
            >
              <TooltipTrigger asChild>
                <button
                  ref={previewTriggerRef}
                  type='button'
                  aria-label={`Preview ${fullTitle}`}
                  onClick={(e) => e.stopPropagation()}
                  onPointerEnter={openPreview}
                  onPointerLeave={clearPreviewOpenTimer}
                  className={cn(
                    'flex h-6 w-7 items-center justify-center rounded',
                    'text-neutral-500 hover:bg-neutral-200/80 hover:text-neutral-800',
                    'cursor-pointer'
                  )}
                >
                  <InfoIcon className='h-3.5 w-3.5' strokeWidth={2} />
                </button>
              </TooltipTrigger>
              <TooltipPortal container={getDrumkitContainer()}>
                <TooltipContent
                  side='bottom'
                  align='end'
                  alignOffset={previewLayout.alignOffsetPx}
                  // Keep measured bounds - collision shifting crosses the category
                  // tree line / info column at 320px sidebar widths.
                  avoidCollisions={false}
                  className='max-h-[400px] p-0 shadow-lg'
                  style={{ width: previewLayout.widthPx }}
                >
                  <SuggestionPreview
                    suggestion={suggestion}
                    ordinal={ordinal}
                    descriptor={descriptor}
                  />
                </TooltipContent>
              </TooltipPortal>
            </Tooltip>
          )}

          {dismissible && (
            <DropdownMenu
              open={menuOpen}
              onOpenChange={(open) => {
                setMenuOpen(open);
                if (open) onPreviewOpenChange(false);
              }}
            >
              <DropdownMenuTrigger asChild>
                <button
                  type='button'
                  aria-label={`More actions for ${fullTitle}`}
                  onClick={(e) => e.stopPropagation()}
                  className={cn(
                    'flex h-6 w-5 items-center justify-center rounded',
                    'text-neutral-500 hover:bg-neutral-200/80 hover:text-neutral-800',
                    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-400',
                    'cursor-pointer'
                  )}
                >
                  <EllipsisVertical className='h-3.5 w-3.5' strokeWidth={2} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align='end'
                sideOffset={4}
                className='min-w-32'
              >
                <DropdownMenuItem
                  disabled={isDismissing}
                  onSelect={handleDismiss}
                  className='cursor-pointer text-neutral-700 focus:text-neutral-900'
                >
                  <X className='mr-1.5 h-3.5 w-3.5' />
                  <Typography variant='body-xs'>
                    {isDismissing ? 'Dismissing...' : 'Dismiss'}
                  </Typography>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
    </div>
  );
}
